import { useState } from "react";
import { ChevronDown } from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Cookies from "js-cookie";
import { toast } from "sonner";
import { AxiosError } from "axios";

import { cn } from "@/lib/utils";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu/dropdown-menu";
import { changeAttendanceStatusByAdmin } from "@/config/mutation/employee";
import { getCurrentAdmin } from "@/config/queries/admin/admin-queries";
import { AttendanceChangeModal } from "@/components/attendance-change-modal/attendance-change-modal";

export type Attendance = {
  status: string;
  date: string | Date;
  id: string;
  checkinTime: string;
  checkoutTime: string;
  hoursWorked: number;
  employeeName?: string;
};

interface Props {
  attendance: Attendance;
  mutateCalendarData: () => void;
  employeeName?: string;
}

const ATTENDANCE_COLORS: {
  [key: string]: string;
} = {
  "Half-day": "#FFFF00",
  Present: "#00FF00",
  Absent: "#FF0000",
  "Paid Leave": "#00CED1",
  "On Leave": "#FFA500",
  "Not Counting": "#808080",
};

const ATTENDANCE_FOREGROUNDS: {
  [key: string]: string;
} = {
  "Half-day": "black",
  Present: "black",
  Absent: "black",
  "Paid Leave": "black",
  "On Leave": "black",
  "Not Counting": "black",
};

export const AttendanceEvent = ({ attendance, mutateCalendarData, employeeName }: Props) => {
  const queryClient = useQueryClient();

  const [isOpen, setIsOpen] = useState(false);

  // State for attendance change confirmation modal
  const [isChangeModalOpen, setIsChangeModalOpen] = useState(false);
  const [pendingStatus, setPendingStatus] = useState<string | null>(null);

  const key = Cookies.get("key") as string;
  const colorAtt = ATTENDANCE_FOREGROUNDS[attendance.status];
  const bgAtt = ATTENDANCE_COLORS[attendance.status];

  const [position, setPosition] = useState(attendance?.status);

  // Fetch current admin info
  const { data: adminData } = useQuery({
    queryKey: ["current-admin"],
    queryFn: () => getCurrentAdmin(key),
  });

  const { mutate: ChangeStatus, isPending: isChanging } = useMutation({
    mutationFn: changeAttendanceStatusByAdmin(key),
  });

  // Handler to open confirmation modal
  const handleStatusSelect = (newStatus: string) => {
    if (newStatus === attendance?.status) return;
    setPendingStatus(newStatus);
    setIsChangeModalOpen(true);
    setIsOpen(false); // Close dropdown
  };

  // Handler for confirmed status change
  const onStatusChangeConfirmed = async (reason: string) => {
    if (!attendance?.id || !pendingStatus) return;

    ChangeStatus(
      {
        id: attendance.id,
        status: pendingStatus,
        reason: reason,
        acknowledgedChange: true,
      },
      {
        onSuccess: async () => {
          await queryClient.invalidateQueries({
            queryKey: ["attendance-with-monthyear"],
          });

          mutateCalendarData();

          setIsChangeModalOpen(false);
          setPendingStatus(null);
          toast.success("Attendance Updated!");
        },
        onError: (error) => {
          console.log("ERROR WHILE CHANGING ATTENDANCE STATUS", error);
          const isAxiosError = error instanceof AxiosError;
          if (isAxiosError) {
            const errorReason = error?.response?.data?.error as string;
            toast.error(errorReason);
          } else {
            toast.error(error?.message);
          }
        },
      }
    );
  };

  return (
    <div>
      <div
        style={{
          backgroundColor: bgAtt,
          color: colorAtt,
        }}
      >
        <div
          className={cn(
            "flex items-center justify-between px-2 py-1",
            colorAtt
          )}
        >
          <div className="text-sm">{attendance?.status}</div>

          <div>
            <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
              <DropdownMenuTrigger asChild>
                <button>
                  <ChevronDown className="w-3 h-3" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-56">
                <DropdownMenuLabel>Attendance Status</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuRadioGroup
                  value={position}
                  onValueChange={setPosition}
                >
                  <DropdownMenuRadioItem
                    value="Present"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      handleStatusSelect("Present");
                    }}
                    // Restriction: Cannot change Half-day to Present
                    disabled={isChanging || attendance?.status === "Half-day"}
                    title={attendance?.status === "Half-day" ? "Cannot upgrade Half-day to Present" : undefined}
                  >
                    Present
                  </DropdownMenuRadioItem>
                  <DropdownMenuRadioItem
                    value="Absent"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      handleStatusSelect("Absent");
                    }}
                    disabled={isChanging}
                  >
                    Absent
                  </DropdownMenuRadioItem>
                  <DropdownMenuRadioItem
                    value="Half-day"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      handleStatusSelect("Half-day");
                    }}
                    disabled={isChanging}
                  >
                    Half Day
                  </DropdownMenuRadioItem>
                  <DropdownMenuRadioItem
                    value="Paid Leave"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      handleStatusSelect("Paid Leave");
                    }}
                    disabled={isChanging}
                  >
                    Paid Leave
                  </DropdownMenuRadioItem>
                  <DropdownMenuRadioItem
                    value="On Leave"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      handleStatusSelect("On Leave");
                    }}
                    disabled={isChanging}
                  >
                    Unpaid Leave
                  </DropdownMenuRadioItem>
                  <DropdownMenuRadioItem
                    value="Holiday"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      handleStatusSelect("Holiday");
                    }}
                    disabled={isChanging}
                  >
                    Holiday
                  </DropdownMenuRadioItem>
                </DropdownMenuRadioGroup>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>

      <div className="bg-white flex flex-col text-black px-1 max-w-full flex-wrap">
        <p className="text-[11px]">Checkin - {attendance?.checkinTime}</p>
        <p className="text-[11px]">Checkout - {attendance?.checkoutTime}</p>
        {/* <p className="text-[11px]">Hours - {attendance?.hoursWorked}</p> */}
      </div>

      {/* Attendance Change Confirmation Modal */}
      <AttendanceChangeModal
        isOpen={isChangeModalOpen}
        onClose={() => {
          setIsChangeModalOpen(false);
          setPendingStatus(null);
        }}
        onConfirm={onStatusChangeConfirmed}
        isLoading={isChanging}
        adminName={adminData?.foundAdmin?.name || "Admin"}
        employeeName={employeeName || attendance?.employeeName || "Employee"}
        currentStatus={attendance?.status}
        newStatus={pendingStatus || ""}
        attendanceDate={attendance?.date}
      />
    </div>
  );
};
